import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, Upload, FileText, Music, Image as ImageIcon, Plus, Download,
  Trash2, Loader2, ExternalLink, X, AlertTriangle, Calendar, Tag,
} from 'lucide-react';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { useLanguage } from '../context/LanguageContext';

/**
 * e-Dépôts panel — lives inside the Profile page (own profile only).
 * Lets the user list, create, and delete their numerical deposits.
 *
 * UX considerations applied (mirrors what we did for Messages.jsx):
 *  - Modal scrolls with `overflow-y-auto` AND respects the keyboard via
 *    `marginBottom: keyboardHeight + 20` so input fields never sit under the
 *    on-screen keyboard.
 *  - Each interactive element has data-testid for the test agent.
 */

const WORK_TYPES = [
  { value: 'audiovisuel', label: 'Œuvre audiovisuelle' },
  { value: 'radio', label: 'Œuvre radiophonique' },
  { value: 'cinema_scenario', label: 'Scénario / cinéma' },
  { value: 'multimedia', label: 'Œuvre multimédia' },
  { value: 'image_fixe', label: 'Image fixe' },
  { value: 'photographie', label: 'Photographie' },
  { value: 'spectacle_vivant', label: 'Œuvre de spectacle vivant' },
  { value: 'musique', label: 'Œuvre musicale' },
  { value: 'litteraire', label: 'Œuvre littéraire' },
  { value: 'graphique', label: 'Œuvre graphique' },
];

const fileIcon = (mime) => {
  if (!mime) return FileText;
  if (mime.startsWith('audio/')) return Music;
  if (mime.startsWith('image/')) return ImageIcon;
  return FileText;
};

const formatBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
};

const EDepotsSection = ({ currentUser }) => {
  const { t } = useLanguage();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDeleteFor, setShowDeleteFor] = useState(null); // deposit object
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/edepots/me');
      setDeposits(res.data?.deposits || []);
    } catch (e) {
      toast.error("Impossible de charger vos dépôts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Detect /edepots/success?deposit_id=... and confirm the payment.
  useEffect(() => {
    const search = window.location.search;
    if (!search.includes('deposit_id=')) return;
    const params = new URLSearchParams(search);
    const did = params.get('deposit_id');
    const isSuccess = window.location.pathname.includes('/success') || search.includes('success');
    if (!did || !isSuccess) return;
    (async () => {
      try {
        await axios.post(`/api/edepots/${did}/confirm-payment`);
        toast.success('Paiement confirmé — votre dépôt est enregistré.');
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Confirmation échouée');
      } finally {
        // Clean the URL so subsequent visits don't re-trigger this.
        window.history.replaceState({}, '', '/profile');
      }
    })();
  }, []);

  const downloadCertificate = async (d) => {
    setBusy(true);
    try {
      const res = await axios.get(`/api/edepots/${d.id}/certificate`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_${d.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Téléchargement impossible');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="edepots-section">
      {/* Header card with brief explanation */}
      <div className="rounded-2xl border border-fuchsia-500/30 bg-fuchsia-500/5 p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-6 h-6 text-fuchsia-400 shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-white">{t('edepots.titleHeader')}</h3>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              {t('edepots.subHeader')}
            </p>
            <a
              href="https://info.destine.studio"
              target="_blank" rel="noopener noreferrer"
              className="text-xs text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center gap-1 mt-2"
              data-testid="edepots-info-link"
            >
              {t('common.learn_more') || 'En savoir plus'} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="mt-4 w-full bg-fuchsia-600 hover:bg-fuchsia-700 active:bg-fuchsia-800 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
          data-testid="edepots-create-btn"
        >
          <Plus className="w-4 h-4" /> {t('edepots.newButton')}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : deposits.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">{t('edepots.noDeposits')}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {deposits.map((d) => {
            const Icon = fileIcon(d.file_mime);
            const isPending = d.status !== 'paid';
            return (
              <div
                key={d.id}
                className={`rounded-xl border p-3.5 flex items-center gap-3 ${
                  isPending ? 'border-amber-500/40 bg-amber-500/5' : 'border-white/10 bg-white/5'
                }`}
                data-testid={`edepot-row-${d.id}`}
              >
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${
                  isPending ? 'bg-amber-500/15' : 'bg-fuchsia-500/15'
                }`}>
                  <Icon className={`w-5 h-5 ${isPending ? 'text-amber-300' : 'text-fuchsia-300'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{d.title}</p>
                  <p className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                    <Tag className="w-3 h-3" />
                    {WORK_TYPES.find(w => w.value === d.work_type)?.label || d.work_type}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    {d.deposited_at ? new Date(d.deposited_at).toLocaleDateString('fr-FR') : 'En attente de paiement'}
                    {' · '}{formatBytes(d.file_size)}
                  </p>
                  {isPending && (
                    <p className="text-[11px] text-amber-300 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Paiement en attente
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  {!isPending && (
                    <button
                      onClick={() => downloadCertificate(d)}
                      disabled={busy}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors text-fuchsia-300 disabled:opacity-50"
                      title="Télécharger l'attestation"
                      data-testid={`edepot-download-${d.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setShowDeleteFor(d)}
                    className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-red-400"
                    title="Supprimer"
                    data-testid={`edepot-delete-${d.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateDepositModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {showDeleteFor && (
        <DeleteDepositModal
          deposit={showDeleteFor}
          currentUser={currentUser}
          onClose={() => setShowDeleteFor(null)}
          onDeleted={() => { setShowDeleteFor(null); load(); }}
        />
      )}
    </div>
  );
};


const CreateDepositModal = ({ onClose, onCreated }) => {
  const keyboardHeight = useKeyboardHeight();
  const [step, setStep] = useState(1); // 1: form, 2: uploading, 3: redirecting
  const [title, setTitle] = useState('');
  const [workType, setWorkType] = useState('litteraire');
  const [description, setDescription] = useState('');
  const [durationYears, setDurationYears] = useState(20);
  const [coAuthorName, setCoAuthorName] = useState('');
  const [coAuthorEmail, setCoAuthorEmail] = useState('');
  const [file, setFile] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!file) { toast.error('Veuillez sélectionner un fichier'); return; }
    if (!title.trim()) { toast.error('Veuillez renseigner un titre'); return; }
    setSubmitting(true);
    setStep(2);
    try {
      // Step 1: upload file
      let uploaded = uploadedFile;
      if (!uploaded) {
        const fd = new FormData();
        fd.append('file', file);
        const upRes = await axios.post('/api/edepots/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded = upRes.data;
        setUploadedFile(uploaded);
      }

      // Step 2: create deposit + Stripe session
      setStep(3);
      const coAuthors = coAuthorName.trim()
        ? [{ name: coAuthorName.trim(), email: coAuthorEmail.trim() || null }]
        : [];
      const createRes = await axios.post('/api/edepots', {
        title: title.trim(),
        work_type: workType,
        description: description.trim() || null,
        duration_years: Number(durationYears),
        co_authors: coAuthors,
        file_id: uploaded.file_id,
        file_name: uploaded.file_name,
        file_size: uploaded.file_size,
        file_hash_sha256: uploaded.file_hash_sha256,
        file_mime: uploaded.file_mime,
      });
      // Redirect to Stripe Checkout
      window.location.href = createRes.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création du dépôt');
      setStep(1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto pt-8 pb-4 px-4"
      data-testid="edepot-create-modal"
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full"
        style={{ marginBottom: keyboardHeight > 0 ? keyboardHeight + 20 : 0 }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Shield className="w-5 h-5 text-fuchsia-400" /> Nouveau dépôt
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="edepot-modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {step === 1 && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5">Titre de l'œuvre</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex. La polygamie est une bonne chose"
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500 transition-colors"
                data-testid="edepot-title-input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5">Type d'œuvre</label>
              <select
                value={workType}
                onChange={(e) => setWorkType(e.target.value)}
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-fuchsia-500 transition-colors"
                data-testid="edepot-worktype-select"
              >
                {WORK_TYPES.map(w => <option key={w.value} value={w.value} className="bg-gray-900">{w.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5">Description (facultatif)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Quelques lignes pour décrire votre œuvre…"
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500 transition-colors resize-none"
                data-testid="edepot-description-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5">
                Durée de conservation : <span className="text-fuchsia-300">{durationYears} ans</span>
              </label>
              <input
                type="range"
                min={10} max={50} step={1}
                value={durationYears}
                onChange={(e) => setDurationYears(e.target.value)}
                className="w-full accent-fuchsia-500"
                data-testid="edepot-duration-slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>10 ans</span><span>50 ans</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold mb-1.5">Co-auteur (nom)</label>
                <input
                  type="text"
                  value={coAuthorName}
                  onChange={(e) => setCoAuthorName(e.target.value)}
                  placeholder="Facultatif"
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500 transition-colors"
                  data-testid="edepot-coauthor-name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Email du co-auteur</label>
                <input
                  type="email"
                  value={coAuthorEmail}
                  onChange={(e) => setCoAuthorEmail(e.target.value)}
                  placeholder="Facultatif"
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500 transition-colors"
                  data-testid="edepot-coauthor-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5">Fichier de l'œuvre</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,audio/*,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="edepot-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-3 py-3 bg-white/5 border border-dashed border-white/20 rounded-xl text-white hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                data-testid="edepot-pick-file"
              >
                <Upload className="w-4 h-4" />
                {file ? `${file.name} (${formatBytes(file.size)})` : 'Choisir un fichier (PDF, audio ou image)'}
              </button>
              <p className="text-[11px] text-gray-500 mt-1">PDF max 50 MB · Audio max 60 MB · Image max 15 MB</p>
            </div>

            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between text-sm mb-3">
                <span className="text-gray-400">Coût du dépôt</span>
                <span className="text-white font-semibold">10,00 €</span>
              </div>
              <button
                type="submit"
                disabled={submitting || !file || !title.trim()}
                className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                data-testid="edepot-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                Procéder au paiement (10 €)
              </button>
            </div>
          </form>
          )}

          {step >= 2 && (
            <div className="py-8 text-center space-y-4" data-testid="edepot-uploading-state">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-fuchsia-400" />
              <p className="text-sm text-gray-300">
                {step === 2 ? 'Téléchargement et empreinte du fichier…' : 'Redirection vers le paiement sécurisé…'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const DeleteDepositModal = ({ deposit, currentUser, onClose, onDeleted }) => {
  const keyboardHeight = useKeyboardHeight();
  const [dob, setDob] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!dob) { toast.error('Veuillez renseigner votre date de naissance'); return; }
    setBusy(true);
    try {
      await axios.delete(`/api/edepots/${deposit.id}`, {
        data: { date_of_birth: dob },
      });
      toast.success('Dépôt supprimé');
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression impossible');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto pt-8 pb-4 px-4"
      data-testid="edepot-delete-modal"
    >
      <div
        className="bg-gray-900 border border-red-500/30 rounded-3xl max-w-md w-full"
        style={{ marginBottom: keyboardHeight > 0 ? keyboardHeight + 20 : 0 }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" /> Confirmer la suppression
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full"><X className="w-5 h-5" /></button>
          </div>
          <p className="text-sm text-gray-300 mb-4">
            Pour supprimer « <span className="text-white font-semibold">{deposit.title}</span> », veuillez
            confirmer votre date de naissance enregistrée à l'inscription.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5">Date de naissance</label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-red-500"
                data-testid="edepot-delete-dob"
                required
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
              data-testid="edepot-delete-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Supprimer définitivement
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};


export default EDepotsSection;
